import { Phone, MapPin, Clock, ArrowUpRight, Mail } from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TrustStrip } from "../components/TrustStrip";
import { ContactForm } from "../components/ContactForm";
import { Reveal, Eyebrow } from "../components/Reveal";
import { BIZ, track } from "../data/site";
import { Img } from "../components/Img";

export default function ContactPage() {
  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Westway Collision Center — Carrollton, TX"
        description="Contact Westway Collision Center: 1420 Westway Cir, Carrollton, TX 75006. Call (214) 888-2886. Mon–Fri 7:30–6, Sat 8–5. Free estimates & towing."
        path="/contact"
        schemas={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]}
      />

      <section className="relative pt-32 lg:pt-40 pb-14 overflow-hidden">
        <div className="absolute inset-0 surface-weave opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]} />
          <Reveal>
            <Eyebrow>Talk To a Human</Eyebrow>
            <h1 className="mt-5 font-display text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.06] text-balance max-w-4xl">
              Contact Westway Collision Center — Carrollton, TX
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed">
              Call, visit, or write — every route reaches a master technician, not a call center.
              Free estimates, free towing dispatch, and direct insurance help all start here.
            </p>
          </Reveal>
        </div>
      </section>

      <TrustStrip />

      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
          <Reveal>
            <div className="grid grid-cols-1 gap-5 h-full">
              <a
                href={BIZ.phoneHref}
                data-testid="contact-page-call-card"
                onClick={() => track("click_to_call", { location: "contact_page" })}
                className="group rounded-2xl border border-white/[0.08] bg-surface p-7 transition-all duration-500 hover:border-accent/30 hover:-translate-y-1"
              >
                <Phone size={22} className="text-accent" strokeWidth={1.8} aria-hidden="true" />
                <h2 className="mt-4 font-mono text-[10px] tracking-[0.28em] uppercase text-mute">Call or Text</h2>
                <p className="mt-2 font-display text-2xl font-bold text-white group-hover:text-accent transition-colors">{BIZ.phoneDisplay}</p>
                <p className="mt-1 text-sm text-slate-400">Fastest for towing and urgent damage</p>
              </a>
              <a
                href={BIZ.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-page-address-card"
                className="group rounded-2xl border border-white/[0.08] bg-surface p-7 transition-all duration-500 hover:border-accent/30 hover:-translate-y-1"
              >
                <MapPin size={22} className="text-accent" strokeWidth={1.8} aria-hidden="true" />
                <h2 className="mt-4 font-mono text-[10px] tracking-[0.28em] uppercase text-mute">Visit the Facility</h2>
                <p className="mt-2 text-base sm:text-lg text-slate-200">{BIZ.street}<br />{BIZ.city}, {BIZ.state} {BIZ.zip}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-accent/80 group-hover:text-accent transition-colors">
                  Get directions <ArrowUpRight size={12} />
                </span>
              </a>
              <div className="rounded-2xl border border-white/[0.08] bg-surface p-7">
                <Clock size={22} className="text-accent" strokeWidth={1.8} aria-hidden="true" />
                <h2 className="mt-4 font-mono text-[10px] tracking-[0.28em] uppercase text-mute">Shop Hours</h2>
                <ul className="mt-2 space-y-1.5 text-sm sm:text-base text-slate-200">
                  <li className="flex justify-between gap-4"><span>Mon – Fri</span><span className="text-slate-400">7:30 AM – 6:00 PM</span></li>
                  <li className="flex justify-between gap-4"><span>Saturday</span><span className="text-slate-400">8:00 AM – 5:00 PM</span></li>
                  <li className="flex justify-between gap-4"><span>Sunday</span><span className="text-slate-400">Closed</span></li>
                </ul>
                <a href={`mailto:${BIZ.email}`} data-testid="contact-page-email" className="mt-4 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-accent transition-colors">
                  <Mail size={14} className="text-accent" /> {BIZ.email}
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full min-h-80 overflow-hidden rounded-2xl border border-white/[0.08]">
              <iframe
                title="Map to Westway Collision Center, 1420 Westway Cir, Carrollton, TX 75006"
                src="https://www.google.com/maps?q=1420+Westway+Cir,+Carrollton,+TX+75006&output=embed"
                className="h-full w-full min-h-96 grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="clip-frame overflow-hidden rounded-sm border border-white/10">
              <Img name="westway-collision-shop-interior" alt="Inside the Westway Collision Center shop in Carrollton, TX" className="h-56 sm:h-72 w-full object-cover" sizes="100vw" />
            </div>
          </Reveal>
        </div>
      </section>

      <ContactForm />
    </div>
  );
}
